import { createSlice } from "@reduxjs/toolkit";
import {
  getAddendumsThunk,
  getAddendumByIdThunk,
  getAddendumsByPKSThunk,
  getAddendumsByContractThunk,
  createAddendumThunk,
  updateAddendumThunk,
  deleteAddendumThunk,
  submitAddendumThunk,
  approveAddendumThunk,
  rejectAddendumThunk,
  activateAddendumThunk,
} from "state/action/addendum";

// ── State awal form create/update — sinkron dengan models/addendum.js ─────────
export const initialAddendumForm = {
  pks_id: "",
  contract_id: "",

  // ── Identitas Dokumen ──────────────────────────────────────────────────────
  title: "",                // label singkat (UI helper, tidak ada di model baru)
  judul_dokumen: "",        // "Addendum I Perjanjian Kerja Sama..."
  nomor_adendum: "",        // urutan numerik: 1, 2, 3 (auto-generate di BE)

  // ── Tipe & Deskripsi ──────────────────────────────────────────────────────
  type: "",                 // "contract_extension" | "price_adjustment" | ...
  description: "",
  notes: "",

  // ── Tanggal ────────────────────────────────────────────────────────────────
  effective_date: "",
  tanggal_tanda_tangan: "",

  // ── Nomor Referensi Surat ──────────────────────────────────────────────────
  nomor_referensi: {
    mrtj: "",               // "CON/006-01/MRT/III/2019"
    bank_dki: "",           // "06/PKS/DIR/III/2019"
  },

  // ── Para Pihak ────────────────────────────────────────────────────────────
  para_pihak: [
    {
      pihak: "MRTJ",
      nama_perusahaan: "PT MRT Jakarta (Perseroda)",
      nama_perwakilan: "",
      jabatan: "",
      dasar_hukum_pendirian: "",
      dasar_hukum_kewenangan: "",
    },
    {
      pihak: "Bank",
      nama_perusahaan: "",
      nama_perwakilan: "",
      jabatan: "",
      dasar_hukum_pendirian: "",
      dasar_hukum_kewenangan: "",
    },
  ],

  // ── Latar Belakang ────────────────────────────────────────────────────────
  latar_belakang: [
    { poin: "A", deskripsi: "", dokumen_terkait: "" },
  ],

  // ── Amendemen Pasal (INTI) ────────────────────────────────────────────────
  amendemen_pasal: [
    { pasal_referensi: "", topik_perubahan: "", isi_perubahan: "", nilai_baru: "" },
  ],

  // ── Perubahan Terstruktur (audit trail, query sistem) ─────────────────────
  changes: {
    contract_value: { old_value: "", new_value: "" },
    contract_dates: {
      old_start_date: "",
      new_start_date: "",
      old_end_date: "",
      new_end_date: "",
    },
    pricing: { old_prices: null, new_prices: null },
    scope: { added_items: [], removed_items: [], modified_items: [] },
    custom: null,
  },

  // ── Lampiran ──────────────────────────────────────────────────────────────
  lampiran: {
    documents: [],
    media_sosialisasi: [],
    pedoman_pelaksanaan_pembayaran: {
      terdapat_pedoman: false,
      detail_ketentuan: "",
    },
  },
};


// ── Helper untuk state async ──────────────────────────────────────────────────
const asyncState = (data = null) => ({
  data,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
});

const pending = (s) => ({ ...s, isPending: true, isFulfilled: false, isRejected: false, err: null });
const fulfilled = (s, data) => ({ ...s, isPending: false, isFulfilled: true, data });
const rejected = (s, err) => ({ ...s, isPending: false, isRejected: true, err });

// ── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  // List semua addendum
  getAddendums: asyncState(),
  // Addendum by PKS
  getAddendumsByPKS: asyncState(),
  // Addendum by Contract
  getAddendumsByContract: asyncState(),
  // Single addendum (detail)
  getAddendumById: asyncState(),
  // Create
  createAddendum: asyncState(),
  // Update
  updateAddendum: asyncState(),
  // Delete
  deleteAddendum: asyncState(),
  // Submit (workflow)
  submitAddendum: asyncState(),
  // Approve (workflow)
  approveAddendum: asyncState(),
  // Reject (workflow)
  rejectAddendum: asyncState(),
  // Activate (workflow)
  activateAddendum: asyncState(),

  // Form input body untuk create / edit
  addendumForm: { ...initialAddendumForm },

  // Addendum yang sedang di-preview / dipilih
  selectedAddendum: null,
};

export const addendumSlice = createSlice({
  name: "addendum",
  initialState,
  reducers: {
    // ── Form management ─────────────────────────────────────────────────────
    setAddendumForm: (state, action) => {
      state.addendumForm = action.payload;
    },
    updateAddendumFormField: (state, action) => {
      const { field, value } = action.payload;
      state.addendumForm[field] = value;
    },
    updateAddendumChangesField: (state, action) => {
      const { section, field, value } = action.payload;
      if (section) {
        state.addendumForm.changes[section][field] = value;
      } else {
        state.addendumForm.changes[field] = value;
      }
    },
    resetAddendumForm: (state) => {
      state.addendumForm = { ...initialAddendumForm };
    },

    // ── Selected addendum ────────────────────────────────────────────────────
    setSelectedAddendum: (state, action) => {
      state.selectedAddendum = action.payload;
    },
    clearSelectedAddendum: (state) => {
      state.selectedAddendum = null;
    },

    // ── Clear async state ────────────────────────────────────────────────────
    clearGetAddendums: (state) => { state.getAddendums = asyncState(); },
    clearGetAddendumsByPKS: (state) => { state.getAddendumsByPKS = asyncState(); },
    clearGetAddendumsByContract: (state) => { state.getAddendumsByContract = asyncState(); },
    clearGetAddendumById: (state) => { state.getAddendumById = asyncState(); },
    clearCreateAddendum: (state) => { state.createAddendum = asyncState(); },
    clearUpdateAddendum: (state) => { state.updateAddendum = asyncState(); },
    clearDeleteAddendum: (state) => { state.deleteAddendum = asyncState(); },
    clearWorkflowStates: (state) => {
      state.submitAddendum = asyncState();
      state.approveAddendum = asyncState();
      state.rejectAddendum = asyncState();
      state.activateAddendum = asyncState();
    },
  },
  extraReducers(builder) {
    // ── GET ALL ──────────────────────────────────────────────────────────────
    builder
      .addCase(getAddendumsThunk.pending, (state) => {
        state.getAddendums = pending(state.getAddendums);
      })
      .addCase(getAddendumsThunk.fulfilled, (state, action) => {
        state.getAddendums = fulfilled(state.getAddendums, action.payload);
      })
      .addCase(getAddendumsThunk.rejected, (state, action) => {
        state.getAddendums = rejected(state.getAddendums, action.error.message);
      });

    // ── GET BY PKS ───────────────────────────────────────────────────────────
    builder
      .addCase(getAddendumsByPKSThunk.pending, (state) => {
        state.getAddendumsByPKS = pending(state.getAddendumsByPKS);
      })
      .addCase(getAddendumsByPKSThunk.fulfilled, (state, action) => {
        state.getAddendumsByPKS = fulfilled(state.getAddendumsByPKS, action.payload);
      })
      .addCase(getAddendumsByPKSThunk.rejected, (state, action) => {
        state.getAddendumsByPKS = rejected(state.getAddendumsByPKS, action.error.message);
      });

    // ── GET BY CONTRACT ──────────────────────────────────────────────────────
    builder
      .addCase(getAddendumsByContractThunk.pending, (state) => {
        state.getAddendumsByContract = pending(state.getAddendumsByContract);
      })
      .addCase(getAddendumsByContractThunk.fulfilled, (state, action) => {
        state.getAddendumsByContract = fulfilled(state.getAddendumsByContract, action.payload);
      })
      .addCase(getAddendumsByContractThunk.rejected, (state, action) => {
        state.getAddendumsByContract = rejected(state.getAddendumsByContract, action.error.message);
      });

    // ── GET BY ID ────────────────────────────────────────────────────────────
    builder
      .addCase(getAddendumByIdThunk.pending, (state) => {
        state.getAddendumById = pending(state.getAddendumById);
      })
      .addCase(getAddendumByIdThunk.fulfilled, (state, action) => {
        state.getAddendumById = fulfilled(state.getAddendumById, action.payload);
        state.selectedAddendum = action.payload?.data ?? action.payload;
      })
      .addCase(getAddendumByIdThunk.rejected, (state, action) => {
        state.getAddendumById = rejected(state.getAddendumById, action.error.message);
      });

    // ── CREATE ───────────────────────────────────────────────────────────────
    builder
      .addCase(createAddendumThunk.pending, (state) => {
        state.createAddendum = pending(state.createAddendum);
      })
      .addCase(createAddendumThunk.fulfilled, (state, action) => {
        state.createAddendum = fulfilled(state.createAddendum, action.payload);
      })
      .addCase(createAddendumThunk.rejected, (state, action) => {
        state.createAddendum = rejected(state.createAddendum, action.error.message);
      });

    // ── UPDATE ───────────────────────────────────────────────────────────────
    builder
      .addCase(updateAddendumThunk.pending, (state) => {
        state.updateAddendum = pending(state.updateAddendum);
      })
      .addCase(updateAddendumThunk.fulfilled, (state, action) => {
        state.updateAddendum = fulfilled(state.updateAddendum, action.payload);
      })
      .addCase(updateAddendumThunk.rejected, (state, action) => {
        state.updateAddendum = rejected(state.updateAddendum, action.error.message);
      });

    // ── DELETE ───────────────────────────────────────────────────────────────
    builder
      .addCase(deleteAddendumThunk.pending, (state) => {
        state.deleteAddendum = pending(state.deleteAddendum);
      })
      .addCase(deleteAddendumThunk.fulfilled, (state, action) => {
        state.deleteAddendum = fulfilled(state.deleteAddendum, action.payload);
      })
      .addCase(deleteAddendumThunk.rejected, (state, action) => {
        state.deleteAddendum = rejected(state.deleteAddendum, action.error.message);
      });

    // ── SUBMIT ───────────────────────────────────────────────────────────────
    builder
      .addCase(submitAddendumThunk.pending, (state) => {
        state.submitAddendum = pending(state.submitAddendum);
      })
      .addCase(submitAddendumThunk.fulfilled, (state, action) => {
        state.submitAddendum = fulfilled(state.submitAddendum, action.payload);
      })
      .addCase(submitAddendumThunk.rejected, (state, action) => {
        state.submitAddendum = rejected(state.submitAddendum, action.error.message);
      });

    // ── APPROVE ──────────────────────────────────────────────────────────────
    builder
      .addCase(approveAddendumThunk.pending, (state) => {
        state.approveAddendum = pending(state.approveAddendum);
      })
      .addCase(approveAddendumThunk.fulfilled, (state, action) => {
        state.approveAddendum = fulfilled(state.approveAddendum, action.payload);
      })
      .addCase(approveAddendumThunk.rejected, (state, action) => {
        state.approveAddendum = rejected(state.approveAddendum, action.error.message);
      });

    // ── REJECT ───────────────────────────────────────────────────────────────
    builder
      .addCase(rejectAddendumThunk.pending, (state) => {
        state.rejectAddendum = pending(state.rejectAddendum);
      })
      .addCase(rejectAddendumThunk.fulfilled, (state, action) => {
        state.rejectAddendum = fulfilled(state.rejectAddendum, action.payload);
      })
      .addCase(rejectAddendumThunk.rejected, (state, action) => {
        state.rejectAddendum = rejected(state.rejectAddendum, action.error.message);
      });

    // ── ACTIVATE ─────────────────────────────────────────────────────────────
    builder
      .addCase(activateAddendumThunk.pending, (state) => {
        state.activateAddendum = pending(state.activateAddendum);
      })
      .addCase(activateAddendumThunk.fulfilled, (state, action) => {
        state.activateAddendum = fulfilled(state.activateAddendum, action.payload);
      })
      .addCase(activateAddendumThunk.rejected, (state, action) => {
        state.activateAddendum = rejected(state.activateAddendum, action.error.message);
      });
  },
});

export const addendumAction = {
  ...addendumSlice.actions,
  getAddendumsThunk,
  getAddendumByIdThunk,
  getAddendumsByPKSThunk,
  getAddendumsByContractThunk,
  createAddendumThunk,
  updateAddendumThunk,
  deleteAddendumThunk,
  submitAddendumThunk,
  approveAddendumThunk,
  rejectAddendumThunk,
  activateAddendumThunk,
};

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectAddendum = (state) => state.addendum;
export const selectAddendumForm = (state) => state.addendum?.addendumForm;
export const selectSelectedAddendum = (state) => state.addendum?.selectedAddendum;
export const selectAddendumsByPKS = (state) =>
  state.addendum?.getAddendumsByPKS?.data?.data ?? [];

export const selectAddendumsByContract = (state) =>
  state.addendum?.getAddendumsByContract?.data?.data ?? [];

export const selectAddendumsByContractState = (state) =>
  state.addendum?.getAddendumsByContract;

export default addendumSlice.reducer;
